import { bindActionCreators } from '@reduxjs/toolkit';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import type { SubscriptionSearchItem } from '../../@types/AdvertismentSubscription';
import { collectSubscription, create, update } from '../../actions/search-subscription';
import { getGuidFromPath } from '../../helpers/query';
import QueryFilter from '../../helpers/query-filter';
import { ExchangeTypes, useDataExchangeStatus } from '../../hooks/exchange-status';
import { getMarket } from '../../i18n';
import type { RootState } from '../../reducers';
import { Button } from '../../styled-components/components/Button';
import { IconBookmark } from '../../styled-components/components/Icon';
import Modal from '../../styled-components/components/Modal';
import {
  getLastSearchSubscription,
  getQueryFilter,
  getSearchSubscription,
} from '../../utils/reselectors';
import SearchSubscription from '../subscriptions/search-subscription';

const resolveSearchSubsriptionRoute = () => {
  const { marketCode } = getMarket();
  switch (marketCode) {
    case 'NO':
      return '/lister/overvaket-sok';
    default:
      return '/listor/bevakade-sokningar';
  }
};

// same compoent used twice
let blocked = false;

interface SaveSubscriptionButtonProps {
  className?: string;
  testId?: string;
}

const SaveSubscriptionButton = ({ className, testId }: SaveSubscriptionButtonProps) => {
  const { t } = useTranslation(['common', 'search']);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { pathname } = useLocation();
  const id = getGuidFromPath(pathname);
  const requestStatus = useSelector<RootState, number>((s) => s.subscriptions.search.requestStatus);
  const responseStatus = useSelector<RootState, number>(
    (s) => s.subscriptions.search.responseStatus,
  );
  const queryFilter = useSelector(getQueryFilter);
  const subscription = useSelector<RootState, SubscriptionSearchItem | null>((s) =>
    getSearchSubscription(s, id),
  );
  const latestSubscription = useSelector(getLastSearchSubscription);

  const actions = bindActionCreators({ update, create, collectSubscription }, dispatch);

  const [modal, setModal] = useState(false);
  const statusUpdate = useDataExchangeStatus(ExchangeTypes.Updating, [
    requestStatus,
    responseStatus,
  ]);
  const statusCreate = useDataExchangeStatus(ExchangeTypes.Creating, [
    requestStatus,
    responseStatus,
  ]);

  useEffect(() => {
    if (modal) {
      if ((statusUpdate.successful || statusCreate.successful) && latestSubscription) {
        setModal(false);
        actions.collectSubscription(latestSubscription);
      }
    }
  }, [statusUpdate, statusCreate]);

  useEffect(() => {
    if (!blocked && statusCreate.successful && latestSubscription) {
      blocked = true;

      navigate(
        `${resolveSearchSubsriptionRoute()}/${latestSubscription.id}?qf=${btoa(
          new QueryFilter(latestSubscription.query).toString(),
        )}`,
      );
    }
  }, [statusCreate.successful]);

  useEffect(() => {
    if (statusCreate.failed) {
      blocked = false;
    }
  }, [statusCreate.failed]);

  const onUpdate = () => {
    if (subscription && id) {
      const payload = {
        createdAt: '2019-07-03T12:24:41.5453071',
        lastNotifiedAt: '2019-07-03T12:24:41.5453076',
        id,
        name: subscription.name,
        query: queryFilter.toString(),
      };

      actions.update(payload);
    }
  };

  if (!queryFilter) return null;

  let disabled = false;
  if (subscription && queryFilter && queryFilter.isSame(new QueryFilter(subscription.query))) {
    disabled = true;
  }

  return (
    <>
      {modal && subscription && (
        <Modal
          heading={t('updateMonitoring', { ns: 'search' })}
          onClose={() => setModal(false)}
          footerActions={[
            <Button
              key="monitoring-cancel"
              onClick={() => setModal(false)}
              title={t('cancel', { ns: 'common' })}
              $tone="clear"
              $fullWidth
            >
              {t('cancel', { ns: 'common' })}
            </Button>,
            <Button
              key="monitoring-save"
              onClick={onUpdate}
              title={t('Update', { ns: 'search' })}
              $tone="primary"
              $fullWidth
            >
              {t('Update', { ns: 'search' })}
            </Button>,
          ]}
        >
          {t('doYouWantToUpdateMonitoring', { ns: 'search', name: subscription.name })}
        </Modal>
      )}
      {modal && !subscription && (
        <SearchSubscription onClose={() => setModal(false)} query={queryFilter.toString()} />
      )}
      <div className={className ? className : 'action-group-container-alt hidden-lt-md'}>
        <div className="action-group-container-alt-btn">
          <button
            type="button"
            disabled={disabled}
            data-wx-button="light thin full-width"
            title={
              subscription
                ? t('updateMonitoring', { ns: 'search' })
                : t('createMonitoring', { ns: 'search' })
            }
            onClick={() => setModal(true)}
            data-testid={testId}
          >
            <div className="button-icon">
              <IconBookmark $size="20px" />
            </div>
            <div className="button-text">
              {subscription
                ? t('updateMonitoring', { ns: 'search' })
                : t('monitor', { ns: 'search' })}
            </div>
          </button>
        </div>
      </div>
    </>
  );
};

export default SaveSubscriptionButton;
